import { useEffect, useRef, useState, useCallback } from 'react';

// Continuous speech recognition via the Web Speech API.
// Auto-restarts when the browser ends a session (Chrome stops after
// silence), and exposes pause/resume so TTS feedback isn't re-heard
// as a command.
export default function useSpeech(onFinalResult) {
  const SR =
    typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const [supported] = useState(Boolean(SR));
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const enabledRef = useRef(false); // user wants the mic on
  const pausedRef = useRef(false); // temporarily ignoring (e.g. during TTS)
  const callbackRef = useRef(onFinalResult);
  callbackRef.current = onFinalResult;

  const buildRecognition = useCallback(() => {
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onresult = (event) => {
      if (pausedRef.current) return;
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript.trim();
        if (result.isFinal) {
          setInterim('');
          if (text) callbackRef.current(text);
        } else {
          interimText += text + ' ';
        }
      }
      if (interimText) setInterim(interimText.trim());
    };

    rec.onstart = () => {
      setListening(true);
      setError(null);
    };

    rec.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setError('Microphone access denied — allow the mic and press Start.');
        enabledRef.current = false;
        setListening(false);
      }
      // 'no-speech' and 'aborted' are routine; onend handles restart.
    };

    rec.onend = () => {
      setInterim('');
      if (enabledRef.current) {
        // Browser closed the session (silence timeout etc.) — restart.
        setTimeout(() => {
          if (enabledRef.current) {
            try {
              rec.start();
            } catch {
              /* already started */
            }
          }
        }, 250);
      } else {
        setListening(false);
      }
    };

    return rec;
  }, [SR]);

  const start = useCallback(() => {
    if (!SR) return;
    enabledRef.current = true;
    pausedRef.current = false;
    if (!recognitionRef.current) recognitionRef.current = buildRecognition();
    try {
      recognitionRef.current.start();
    } catch {
      /* already started */
    }
  }, [SR, buildRecognition]);

  const stop = useCallback(() => {
    enabledRef.current = false;
    if (recognitionRef.current) recognitionRef.current.stop();
    setListening(false);
    setInterim('');
  }, []);

  // Pause/resume without tearing down the session — used while the app
  // speaks feedback so it doesn't hear itself.
  const pause = useCallback(() => {
    pausedRef.current = true;
  }, []);
  const resume = useCallback(() => {
    pausedRef.current = false;
  }, []);

  useEffect(() => {
    return () => {
      enabledRef.current = false;
      if (recognitionRef.current) recognitionRef.current.stop();
    };
  }, []);

  return { supported, listening, interim, error, start, stop, pause, resume };
}
